import { useCallback, useEffect, useState } from 'react';

const KEY = 'vaad_canvas_cart_v1';

/** Read cart from localStorage (safe). */
const load = () => {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
};

/** Persistent multi-item canvas cart, shared across every "Order canvas"
 * button on the site. Persists to localStorage and broadcasts changes to
 * other components via a custom event. */
export function useCanvasCart() {
  const [cart, setCart] = useState(load);

  useEffect(() => {
    const handler = () => setCart(load());
    window.addEventListener('vaad_cart_change', handler);
    window.addEventListener('storage', handler);
    return () => {
      window.removeEventListener('vaad_cart_change', handler);
      window.removeEventListener('storage', handler);
    };
  }, []);

  const persist = useCallback((next) => {
    try {
      localStorage.setItem(KEY, JSON.stringify(next));
      window.dispatchEvent(new Event('vaad_cart_change'));
    } catch {
      /* ignore quota errors */
    }
    setCart(next);
  }, []);

  const addItem = useCallback((item) => {
    const next = [...load(), { ...item, id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}` }];
    persist(next);
  }, [persist]);

  const removeItem = useCallback((id) => {
    persist(load().filter((i) => i.id !== id));
  }, [persist]);

  const clear = useCallback(() => persist([]), [persist]);

  return { cart, addItem, removeItem, clear };
}
